import { type FC } from "react";

import { CheckIcon } from "@snapchat/snap-design-system-icons";
import { clsx } from "clsx";

import styles from "./StepIndicator.module.css";

interface StepIndicatorProps {
    stepIndex: number;
    totalSteps: number;
    currentStep: number;
    completedSteps: Set<number>;
    accepted: boolean;
    onStepClick: (stepIndex: number) => void;
}

export const StepIndicator: FC<StepIndicatorProps> = ({
    stepIndex,
    totalSteps,
    currentStep,
    completedSteps,
    accepted,
    onStepClick,
}) => {
    const isActive = stepIndex === currentStep;
    const isCompleted = accepted || completedSteps.has(stepIndex);
    const isClickable = accepted || stepIndex <= currentStep || completedSteps.has(stepIndex);
    const showsCheckmark = isCompleted && stepIndex !== currentStep;

    const stepClasses = clsx(
        styles.stepIndicator,
        showsCheckmark && styles.showsCheckmark,
        isCompleted && !showsCheckmark && styles.isCompleted,
        isActive && styles.isActive,
        !isActive && !isCompleted && styles.isInactive,
        !isClickable && styles.notClickable,
    );

    const handleClick = (): void => {
        onStepClick(stepIndex);
    };

    return (
        <button
            className={stepClasses}
            onClick={handleClick}
            disabled={!accepted && !(stepIndex <= currentStep || completedSteps.has(stepIndex))}
            type="button"
            aria-label={`Step ${stepIndex + 1} of ${totalSteps}`}
        >
            {showsCheckmark ? <CheckIcon size={16} /> : <span className={styles.stepNumber}>{stepIndex + 1}</span>}
        </button>
    );
};
